import React, { useState, useEffect } from 'react';
import { processService } from '../../services/api';

/**
 * Code Analysis Form Component
 * Allows user to select process and files for UiPath code analysis within chat
 */
const CodeAnalysisForm = ({ projectId, onSubmit, onCancel, isAnalyzing = false }) => {
  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState('');
  const [selectedScope, setSelectedScope] = useState('full-process');
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [error, setError] = useState(null);
  const [fileFilter, setFileFilter] = useState('');

  // Load processes on mount
  useEffect(() => {
    loadProcesses();
  }, [projectId]);

  // Load files when process is selected
  useEffect(() => {
    if (selectedProcess && selectedScope === 'specific-files') {
      loadFilesForProcess(selectedProcess);
    } else {
      setFiles([]);
      setSelectedFiles([]);
    }
  }, [selectedProcess, selectedScope]);

  const loadProcesses = async () => {
    setIsLoadingProcesses(true);
    setError(null);
    
    try {
      const response = await processService.getProcessesByProject(projectId);
      setProcesses(response.data || []);
      
      // Auto-select first process if available
      if (response.data && response.data.length > 0) {
        setSelectedProcess(response.data[0].id);
      }
    } catch (err) {
      console.error('Error loading processes:', err);
      setError('Failed to load processes');
    } finally {
      setIsLoadingProcesses(false);
    }
  };

  const loadFilesForProcess = async (processId) => {
    setIsLoadingFiles(true);
    setError(null);
    
    try {
      // Get files from uipath_code for this process
      const response = await processService.getProcessFiles(processId);
      setFiles(response.data || []);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load files for process');
      setFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare analysis request
    const analysisRequest = {
      projectId,
      processId: selectedProcess || null,
      scope: selectedScope,
      files: selectedScope === 'specific-files' ? selectedFiles : []
    };

    onSubmit(analysisRequest);
  };

  const handleFileToggle = (filePath) => {
    setSelectedFiles(prev => {
      if (prev.includes(filePath)) {
        return prev.filter(f => f !== filePath);
      } else {
        return [...prev, filePath];
      }
    });
  };

  const handleSelectAllFiles = () => {
    setSelectedFiles(files.map(f => f.filePath));
  };

  const handleDeselectAllFiles = () => {
    setSelectedFiles([]);
  };

  // Filter files based on search text
  const filteredFiles = files.filter(file => 
    file.filePath.toLowerCase().includes(fileFilter.toLowerCase())
  );

  if (isLoadingProcesses) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
        <div className="flex items-center justify-center py-4">
          <div className="text-gray-500">Loading processes...</div>
        </div>
      </div>
    );
  }

  if (processes.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-gray-700 font-medium">⚠️ No Processes Found</span>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          This project has no processes uploaded yet. Please upload a UiPath process first.
        </p>
        <button
          onClick={onCancel}
          className="text-sm px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border border-gray-300"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm">
      <form onSubmit={handleSubmit} className="flex flex-col max-h-[calc(100vh-200px)]">
        {/* Scrollable content area */}
        <div className="overflow-y-auto pr-1">
          {/* Header */}
          <div className="mb-4 pb-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              🔍 Code Analysis Settings
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Configure and run code analysis on your UiPath process
            </p>
          </div>

          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              {error}
            </div>
          )}

          {/* Process Selection */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Select Process
            </label>
            <select
              value={selectedProcess}
              onChange={(e) => setSelectedProcess(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-tqa-blue focus:border-tqa-blue text-sm text-gray-800"
              required
            >
              <option value="">-- Select a Process --</option>
              {processes.map(process => (
                <option key={process.id} value={process.id}>
                  {process.name} {process.technicalName ? `(${process.technicalName})` : ''} 
                  {process.version ? ` v${process.version}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Scope Selection - Compact Toggle */}
          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 block mb-2">
              Analysis Scope
            </label>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-medium transition-colors ${
                selectedScope === 'full-process' ? 'text-tqa-blue' : 'text-gray-500'
              }`}>
                All files
              </span>
              <button
                type="button"
                onClick={() => setSelectedScope(selectedScope === 'full-process' ? 'specific-files' : 'full-process')}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-tqa-blue focus:ring-offset-1 ${
                  selectedScope === 'specific-files' ? 'bg-tqa-blue' : 'bg-gray-300'
                }`}
                title={selectedScope === 'full-process' ? 'Switch to specific files' : 'Switch to full process'}
              >
                <span className="sr-only">Toggle analysis scope</span>
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    selectedScope === 'specific-files' ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-xs font-medium transition-colors ${
                selectedScope === 'specific-files' ? 'text-tqa-blue' : 'text-gray-500'
              }`}>
                Specific files
              </span>
            </div>
          </div>


          {/* File Selection (only for specific-files scope) */}
          {selectedScope === 'specific-files' && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-700">
                  Select Files ({selectedFiles.length} selected)
                </label>
                {files.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllFiles}
                      className="text-xs font-medium text-tqa-blue hover:text-tqa-navy hover:underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleDeselectAllFiles}
                      className="text-xs font-medium text-gray-600 hover:text-gray-700 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* File Filter Input */}
              {files.length > 0 && (
                <div className="mb-2">
                  <input
                    type="text"
                    value={fileFilter}
                    onChange={(e) => setFileFilter(e.target.value)}
                    placeholder="🔍 Filter files..."
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-tqa-blue focus:border-tqa-blue text-sm"
                  />
                  {fileFilter && (
                    <div className="mt-1 text-xs text-gray-500">
                      {filteredFiles.length} of {files.length} files
                    </div>
                  )}
                </div>
              )}

              {isLoadingFiles ? (
                <div className="text-xs text-gray-600 py-3 text-center bg-gray-50 rounded-md border border-gray-200">
                  <div className="inline-block w-4 h-4 border-2 border-tqa-blue border-t-transparent rounded-full animate-spin mr-2"></div>
                  Loading files...
                </div>
              ) : files.length === 0 ? (
                <div className="text-xs text-gray-600 py-3 text-center bg-gray-50 rounded-md border border-gray-200 px-3">
                  ℹ️ No files available for this process.
                </div>
              ) : filteredFiles.length === 0 ? (
                <div className="text-xs text-gray-600 py-3 text-center bg-gray-50 rounded-md border border-gray-200 px-3">
                  🔍 No files match "{fileFilter}"
                </div>
              ) : (
                <div className="border border-gray-300 rounded-md max-h-40 overflow-y-auto bg-white">
                  {filteredFiles.map((file) => (
                    <label
                      key={file.filePath}
                      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.filePath)}
                        onChange={() => handleFileToggle(file.filePath)}
                        className="mr-2 h-4 w-4 text-tqa-blue rounded focus:ring-2 focus:ring-tqa-blue flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800 truncate">
                          {file.filePath}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {selectedScope === 'specific-files' && selectedFiles.length === 0 && files.length > 0 && (
                <div className="mt-2 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1.5 rounded border border-amber-200">
                  ⚠️ Please select at least one file
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 mt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-all disabled:opacity-50"
            disabled={isAnalyzing}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 text-sm font-medium text-white bg-tqa-blue rounded-md hover:bg-tqa-navy transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
            disabled={
              isAnalyzing ||
              !selectedProcess ||
              (selectedScope === 'specific-files' && selectedFiles.length === 0)
            }
          >
            {isAnalyzing ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Analyzing...
              </span>
            ) : (
              '🔍 Start Analysis'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};


export default CodeAnalysisForm;
